import PhoneFormatter from "@/util/PhoneFormatter";

export const PHONE_FORMAT_HINT = "+15551234567";

export interface ProviderWebUserInput {
    email?: string;
    firstName?: string;
    lastName?: string;
    phoneNumber?: string;
    npiNumber?: string;
    canPrescribe?: boolean;
}

export interface ProviderWebUserErrors {
    allBlank: boolean;
    email?: string;
    firstName?: string;
    lastName?: string;
    phoneNumber?: string;
    npiNumber?: string;
}

// Rejects the +1 0xx/1xx area codes the server's length-only check lets through and Cognito refuses.
const usPhonePattern = /^\+1[2-9]\d{9}$/;
const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

export function normalizeProviderWebPhone(phone: string | undefined | null): string {
    if (!phone || !phone.trim().length) return "";
    return PhoneFormatter.formatForTwilio(phone.trim());
}

export function isValidProviderWebPhone(phone: string | undefined | null): boolean {
    return usPhonePattern.test(normalizeProviderWebPhone(phone));
}

export function hasProviderWebUserErrors(errors: ProviderWebUserErrors): boolean {
    return errors.allBlank
        || !!(errors.email || errors.firstName || errors.lastName || errors.phoneNumber || errors.npiNumber);
}

export function validateProviderWebUser(person: ProviderWebUserInput | null | undefined): ProviderWebUserErrors {
    const email = person?.email?.trim() ?? "";
    const firstName = person?.firstName?.trim() ?? "";
    const lastName = person?.lastName?.trim() ?? "";
    const rawPhone = person?.phoneNumber?.trim() ?? "";
    const npiNumber = person?.npiNumber?.trim() ?? "";

    const errors: ProviderWebUserErrors = {
        allBlank: !email && !firstName && !lastName && !rawPhone,
    };

    if (!email) errors.email = "Email is required.";
    else if (!emailPattern.test(email)) errors.email = "Enter a valid email address.";

    if (!firstName) errors.firstName = "First Name is required.";
    if (!lastName) errors.lastName = "Last Name is required.";

    if (!rawPhone) errors.phoneNumber = `Phone Number is required. Enter it as ${PHONE_FORMAT_HINT}.`;
    else if (!isValidProviderWebPhone(rawPhone))
        errors.phoneNumber = `Enter a 10-digit US phone number as ${PHONE_FORMAT_HINT}.`;

    if (person?.canPrescribe) {
        if (!npiNumber) errors.npiNumber = "NPI is required when 'Can Prescribe' is selected.";
        else if (!/^\d{10}$/.test(npiNumber)) errors.npiNumber = "NPI must be exactly 10 digits.";
    }

    return errors;
}
